/**
 * Formulario de inscripcion al evento.
 *
 * Es el unico sitio donde se recogen datos personales, asi que concentra las
 * obligaciones de proteccion de datos: consentimiento explicito sin marcar por
 * defecto, informacion clara de para que se usan y durante cuanto tiempo.
 *
 * Accesibilidad del formulario: cada campo con su etiqueta asociada, errores
 * descritos con texto y enlazados con aria-describedby, y el foco llevado al
 * primer campo con error al enviar. Nada de indicar el error solo con un borde
 * rojo.
 *
 * A esta pantalla se llega escaneando un cartel en la calle, asi que antes de
 * pedir un solo dato dice a que se apunta uno: nombre del evento, cuando es,
 * cuantos puntos tiene y como funciona. Antes era una columna de campos con un
 * titulo encima, y quien llegaba desde el cartel tenia que fiarse.
 */

import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { IDIOMAS, NOMBRE_IDIOMA, type Idioma,
  esCodigoPublico,
} from '@recorregut/shared';
import { useEvento, useRegistrarse, type EventoPublico } from '@/api/consultas.ts';
import { etiquetaFormato, idiomaActivo } from '@/i18n';
import { MensajeError } from '@/componentes/MensajeError.tsx';
import { Signo } from '@/componentes/Logotipo.tsx';

interface Errores {
  nombre?: string;
  responsable?: string;
  telefono?: string;
  num_personas?: string;
  email?: string;
  consentimiento?: string;
}

export function Registro() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  /*
   * El parametro es el CODIGO del evento, no su numero: `/registro/3` invitaba
   * a probar el 2 y el 4. Se comprueba la forma antes de pedir nada, para que
   * una direccion mal tecleada no dispare una consulta.
   */
  const parametros = useParams<{ codigo: string }>();
  const codigo = parametros.codigo ?? '';
  const codigoValido = esCodigoPublico(codigo);

  const consultaEvento = useEvento(codigoValido ? codigo : null);
  const registrarse = useRegistrarse(codigo);

  const [errores, setErrores] = useState<Errores>({});
  const [hecho, setHecho] = useState<{ email: string; correoEnviado: boolean } | null>(null);
  const refFormulario = useRef<HTMLFormElement | null>(null);

  if (!codigoValido) {
    return (
      <div className="contenedor-estrecho py-12">
        <p className="aviso-error">{t('errors.evento_no_encontrado')}</p>
      </div>
    );
  }

  if (hecho) {
    return <Confirmacion hecho={hecho} alEmpezar={() => void navegar('/')} />;
  }

  /*
   * Un codigo con forma valida pero que no existe tiene que decirlo, no pintar
   * el formulario sin ficha del evento. Pasaba ya antes con un numero
   * inexistente (`/registro/999`), pero con el numero habia que acertarlo y con
   * doce caracteres cualquiera lo encuentra: quien teclee mal la direccion del
   * cartel veria un formulario que al enviarlo falla.
   */
  if (consultaEvento.isPending) {
    return (
      <div className="contenedor-estrecho py-12">
        <p role="status" className="text-sm text-tinta-500">
          {t('app.carregant')}
        </p>
      </div>
    );
  }

  if (consultaEvento.isError) {
    return (
      <div className="contenedor-estrecho py-12">
        <MensajeError error={consultaEvento.error} />
      </div>
    );
  }

  const evento = consultaEvento.data;
  const registroCerrado = evento !== undefined && !evento.registro_abierto;

  return (
    <div className="contenedor-estrecho space-y-6 py-8 sm:py-12">
      <header>
        <p className="antetitulo">{t('recorregut.antetitol')}</p>
        <h1 className="titulo-pagina mt-1.5">{t('registre.titol')}</h1>
        <div className="filete" />
        {evento ? <p className="mt-4 text-lg text-tinta-700">{evento.nombre}</p> : null}
      </header>

      {/*
        Ficha del evento antes del formulario. Son los tres datos que decide
        alguien delante de un cartel: de que va, cuando es y cuanto dura.
      */}
      {evento ? <FichaEvento evento={evento} /> : null}

      {registroCerrado ? (
        <p role="alert" className="aviso">
          {t('registre.tancat')}
        </p>
      ) : null}

      <form
        ref={refFormulario}
        noValidate
        className="space-y-5"
        onSubmit={(evento) => {
          evento.preventDefault();
          const datos = new FormData(evento.currentTarget);

          const nombre = String(datos.get('nombre') ?? '').trim();
          const responsable = String(datos.get('responsable') ?? '').trim();
          const telefono = String(datos.get('telefono') ?? '').trim();
          const email = String(datos.get('email') ?? '').trim();
          const numPersonas = Number.parseInt(String(datos.get('num_personas') ?? ''), 10);
          const consentimiento = datos.get('consentimiento') === 'si';
          const idioma = String(datos.get('idioma') ?? idiomaActivo()) as Idioma;

          const nuevos: Errores = {};
          if (nombre.length < 2) nuevos.nombre = t('registre.obligatori');
          if (responsable.length < 2) nuevos.responsable = t('registre.obligatori');
          if (!/^[+()\d][\d\s().-]{5,}$/u.test(telefono)) {
            nuevos.telefono = t('registre.telefonNoValid');
          }
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email)) nuevos.email = t('registre.emailNoValid');
          if (!Number.isInteger(numPersonas) || numPersonas < 1) {
            nuevos.num_personas = t('registre.personesNoValid');
          }
          if (!consentimiento) nuevos.consentimiento = t('registre.consentimentObligatori');

          setErrores(nuevos);
          if (Object.keys(nuevos).length > 0) {
            // Foco al primer campo con problema: sin esto, quien navega por
            // teclado tiene que buscar a ciegas donde esta el error.
            const primero =
              refFormulario.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
            primero?.focus();
            return;
          }

          registrarse.mutate(
            {
              nombre,
              responsable,
              telefono,
              email,
              num_personas: numPersonas,
              idioma,
              consentimiento_datos: true,
            },
            {
              onSuccess: (respuesta) => {
                setHecho({ email, correoEnviado: respuesta.email_enviado });
              },
            },
          );
        }}
      >
        {/*
          Tres grupos y no una columna de nueve campos seguidos. El formulario
          es el mismo; lo que cambia es que se lee como tres preguntas cortas en
          vez de como un tramite.
        */}
        <fieldset className="grupo-campos">
          <legend className="titulo-ficha px-1">{t('registre.grupEquip')}</legend>

          <Campo
            nombre="nombre"
            etiqueta={t('registre.nomEquip')}
            autoComplete="organization"
            error={errores.nombre}
          />
          <Campo
            nombre="responsable"
            etiqueta={t('registre.responsable')}
            autoComplete="name"
            error={errores.responsable}
          />
          <Campo
            nombre="num_personas"
            etiqueta={t('registre.numPersones')}
            tipo="number"
            error={errores.num_personas}
            ayuda={t('registre.menors')}
            extras={{ min: 1, max: 50, defaultValue: 2, inputMode: 'numeric' }}
          />
        </fieldset>

        <fieldset className="grupo-campos">
          <legend className="titulo-ficha px-1">{t('registre.grupContacte')}</legend>
          <p className="text-sm text-tinta-500">{t('registre.grupContacteAjuda')}</p>

          <Campo
            nombre="email"
            etiqueta={t('registre.email')}
            tipo="email"
            autoComplete="email"
            error={errores.email}
          />
          <Campo
            nombre="telefono"
            etiqueta={t('registre.telefon')}
            tipo="tel"
            autoComplete="tel"
            error={errores.telefono}
          />

          <div>
            <label htmlFor="campo-idioma" className="etiqueta">
              {t('registre.idiomaPreferit')}
            </label>
            <select id="campo-idioma" name="idioma" defaultValue={idiomaActivo()} className="campo">
              {IDIOMAS.map((idioma) => (
                <option key={idioma} value={idioma} lang={idioma}>
                  {NOMBRE_IDIOMA[idioma]}
                </option>
              ))}
            </select>
          </div>
        </fieldset>

        <fieldset className="grupo-campos">
          <legend className="titulo-ficha px-1">{t('registre.consentimentTitol')}</legend>

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              name="consentimiento"
              value="si"
              // Sin defaultChecked: una casilla premarcada no es consentimiento
              // valido, la persona tiene que marcarla de forma activa.
              aria-invalid={errores.consentimiento ? 'true' : undefined}
              aria-describedby="detalle-consentimiento"
              className="mt-0.5 size-6 shrink-0 accent-[var(--color-roig-600)]"
            />
            {/*
              El texto del consentimiento es el que facilita la entidad
              responsable y no se reescribe. Se le anaden dos cosas de forma:
              el enlace sobre las palabras que nombran la politica, porque el
              consentimiento solo vale si se puede leer aquello que se acepta, y
              el enfasis del verbo en negrita.

              El verbo va en negrita y no en mayusculas sostenidas: algunos
              lectores de pantalla deletrean letra a letra las palabras escritas
              enteras en mayusculas, con lo que "ACCEPTO" se oiria como una
              sucesion de letras sueltas. En negrita el enfasis visual es el
              mismo y la palabra se pronuncia bien.
            */}
            <span className="text-sm leading-relaxed text-tinta-700">
              <Trans
                i18nKey="registre.consentiment"
                components={{
                  destacat: <strong className="font-semibold text-tinta-900" />,
                  politica: (
                    <Link
                      to="/privacitat"
                      target="_blank"
                      className="text-roig-700 underline underline-offset-2"
                    />
                  ),
                }}
              />
            </span>
          </label>
          <p id="detalle-consentimiento" className="text-xs leading-relaxed text-tinta-500">
            {t('registre.consentimentDetall')}
          </p>
          {errores.consentimiento ? (
            <p role="alert" className="error-campo">
              {errores.consentimiento}
            </p>
          ) : null}
        </fieldset>

        {registrarse.isError ? <MensajeError error={registrarse.error} /> : null}

        <button
          type="submit"
          className="boton-primario w-full"
          disabled={registrarse.isPending || registroCerrado}
        >
          {registrarse.isPending ? t('registre.enviant') : t('registre.enviar')}
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Ficha del evento: cuando es, cuantos puntos tiene y como funciona.
 *
 * La fecha se formatea con Intl y no a mano, para que en cada idioma salga en
 * su orden y con su nombre de mes.
 */
function FichaEvento({ evento }: { evento: EventoPublico }) {
  const { t } = useTranslation();
  const formato = etiquetaFormato();

  const inicio = new Date(evento.fecha_hora_inicio);
  const fin = new Date(evento.fecha_hora_fin);
  const mismoDia = inicio.toDateString() === fin.toDateString();

  const fecha = new Intl.DateTimeFormat(formato, { dateStyle: 'long' }).format(inicio);
  const hora = new Intl.DateTimeFormat(formato, { timeStyle: 'short' });
  const franja = mismoDia
    ? `${fecha}, ${hora.format(inicio)} – ${hora.format(fin)}`
    : `${fecha} – ${new Intl.DateTimeFormat(formato, { dateStyle: 'long' }).format(fin)}`;

  return (
    <div className="tarjeta space-y-4">
      {evento.descripcion ? (
        <p className="text-base leading-relaxed text-tinta-700">{evento.descripcion}</p>
      ) : null}

      <dl className="flex flex-wrap gap-x-8 gap-y-3 border-t border-crema-200 pt-4">
        <div>
          <dt className="text-xs font-semibold tracking-wide text-tinta-500 uppercase">
            {t('registre.quan')}
          </dt>
          {/*
            `dateTime` en un `time`: la fecha legible por maquina, ademas de la
            que se lee en pantalla.
          */}
          <dd className="mt-0.5 text-sm text-tinta-900">
            <time dateTime={evento.fecha_hora_inicio}>{franja}</time>
          </dd>
        </div>
        {evento.total_puntos > 0 ? (
          <div>
            <dt className="text-xs font-semibold tracking-wide text-tinta-500 uppercase">
              {t('registre.puntsDelRecorregut')}
            </dt>
            <dd className="mt-0.5 font-titular text-lg leading-none font-semibold text-tinta-900">
              {new Intl.NumberFormat(formato).format(evento.total_puntos)}
            </dd>
          </div>
        ) : null}
      </dl>

      <div className="border-t border-crema-200 pt-4">
        <h2 className="text-xs font-semibold tracking-wide text-tinta-500 uppercase">
          {t('registre.passos')}
        </h2>
        <ol className="mt-3 space-y-2.5">
          {[t('registre.pas1'), t('registre.pas2'), t('registre.pas3')].map((paso, indice) => (
            <li key={paso} className="flex gap-3 text-sm leading-relaxed text-tinta-700">
              <span
                aria-hidden="true"
                className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full
                           border border-crema-200 bg-crema-100 font-titular text-xs
                           font-semibold text-tinta-700"
              >
                {indice + 1}
              </span>
              <span>{paso}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/**
 * Pantalla de confirmacion.
 *
 * Se separa del formulario porque es un momento distinto: aqui ya no hay nada
 * que rellenar, solo hay que saber que ha salido bien y por donde se sigue.
 */
function Confirmacion({
  hecho,
  alEmpezar,
}: {
  hecho: { email: string; correoEnviado: boolean };
  alEmpezar: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="contenedor-estrecho py-12 sm:py-16">
      <div className="tarjeta text-center">
        <span className="inline-flex text-verd-600">
          <Signo tamano={48} />
        </span>
        <h1 className="titulo-seccion mt-4">{t('registre.fet')}</h1>
        <p className="mx-auto mt-3 max-w-prose text-tinta-700">
          {hecho.correoEnviado
            ? t('registre.fetDetall', { email: hecho.email })
            : t('registre.correuFallat')}
        </p>
        <button type="button" className="boton-primario mt-6 w-full sm:w-auto" onClick={alEmpezar}>
          {t('registre.comencar')}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

interface PropsCampo {
  nombre: string;
  etiqueta: string;
  tipo?: string;
  autoComplete?: string;
  error?: string | undefined;
  /** Texto de apoyo bajo el campo, enlazado con aria-describedby. */
  ayuda?: string | undefined;
  extras?: Record<string, unknown>;
}

function Campo({
  nombre,
  etiqueta,
  tipo = 'text',
  autoComplete,
  error,
  ayuda,
  extras,
}: PropsCampo) {
  const id = `campo-${nombre}`;
  const idError = `${id}-error`;
  const idAyuda = `${id}-ayuda`;

  // El campo se describe con su ayuda y, si lo hay, con su error. Los dos a la
  // vez y en este orden: quien usa lector de pantalla oye primero la condicion
  // y despues que se ha equivocado.
  const descrito = [ayuda ? idAyuda : null, error ? idError : null].filter(Boolean).join(' ');

  return (
    <div>
      <label htmlFor={id} className="etiqueta">
        {etiqueta}
      </label>
      <input
        id={id}
        name={nombre}
        type={tipo}
        {...(autoComplete ? { autoComplete } : {})}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={descrito === '' ? undefined : descrito}
        className={`campo ${error ? 'campo-error' : ''}`}
        {...extras}
      />
      {ayuda ? (
        <p id={idAyuda} className="ayuda-campo">
          {ayuda}
        </p>
      ) : null}
      {error ? (
        <p id={idError} role="alert" className="error-campo">
          {error}
        </p>
      ) : null}
    </div>
  );
}
